import { action, observable } from 'mobx';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import { StateDto } from 'cd-eft/_codegen_/openapi-generator/eft-prjadmin';
import { IEfh2RainHydgsByIdData } from 'cd-eft/models/EFH2/rainfall';
import { StateCountyController } from 'cd-eft/services/eftPrjAdminApi';
import { Efh2ControllerApi, HydroControllerApi } from 'cd-eft/services/eftEfh2Api';
import { Efh2InputParamsQuickHydroToolReq, Efh2OutputParamsQuickHydroTool, Rainfall } from 'cd-eft/_codegen_/openapi-generator/eft-efh2';
import { AxiosResponse } from 'axios';
import { formatRound } from 'cd-eft/utils/mathUtilities';
import { timeOfConcLagCalc } from 'cd-eft/business/EFH2/timeOfConcLagCalc';

type InputType = number | '';

interface ILabelValuePair {
  label: string;
  value: any;
}

interface IHydroCalculatorTableData {
  freq: string;
  rain: InputType;
}
interface IHydroCalculatorFormData {
  state: string;
  county: string;
  drainageArea: InputType;
  runoffCurveNumber: InputType;
  watershedLength: InputType;
  watershedSlope: InputType;
  timeOfConcentration: InputType;
  rainfallDistributionType: string;
  dimensionlessUnitHydrograph: string;
  hydrologyCalculatorTableData: Array<IHydroCalculatorTableData>;
}
interface IresultsData {
  peakFlowQ: number | undefined;
  runoff: number | undefined;
}
interface IresultsDataDatabase {
  freq: string | undefined;
  rain: number | undefined;
  peakFlowQ: number | undefined;
  runoff: number | undefined;
}

const defaultRow: IHydroCalculatorTableData = { freq: '', rain: '' };

export class HydrologyCalculatorVM {
  constructor(props) {
    // Not yet needed
  }
  @observable isCalculating: boolean = false;
  @observable headerSwitch: boolean = false;
  @observable resultsRows: boolean = false;
  @observable resultsRowsData: Array<IresultsData> = []; // With table inputs
  @observable resultsRowsDataUseDatabase: Array<IresultsDataDatabase> = []; // No table inputs
  @observable hydroCalculatorFormData: IHydroCalculatorFormData = {
    state: '',
    county: '',
    drainageArea: '',
    runoffCurveNumber: '',
    watershedLength: '',
    watershedSlope: '',
    timeOfConcentration: '',
    rainfallDistributionType: '',
    dimensionlessUnitHydrograph: '',
    hydrologyCalculatorTableData: [{ freq: '', rain: '' }],
  };

  @observable formValidation = {
    hasError: true,
    state: false,
    county: false,
    drainageArea: false,
    runoffCurveNumber: false,
    watershedLength: false,
    watershedSlope: false,
    timeOfConcentration: false,
    rainfallDistributionType: false,
    dimensionlessUnitHydrograph: false,
    hydrologyCalculatorTableData: [
      {
        freq: true,
        rain: true,
      },
    ],
    formErrors: {
      state: '',
      county: '',
      drainageArea: '',
      runoffCurveNumber: '',
      watershedLength: '',
      watershedSlope: '',
      timeOfConcentration: '',
      rainfallDistributionType: '',
      dimensionlessUnitHydrograph: '',
      hydrologyCalculatorTableData: [
        {
          freq: '',
          rain: '',
        },
      ],
    },
  };
  @observable rainfallOptions = [
    { name: '2_ND', value: '2_ND' },
    { name: '3_ND', value: '3_ND' },
    { name: '4_ND', value: '4_ND' },
    { name: 'AM SAMOA', value: 'AM SAMOA' },
    { name: 'CA-1', value: 'CA-1' },
    { name: 'CA-2', value: 'CA-2' },
    { name: 'CA-3', value: 'CA-3' },
    { name: 'CA-4', value: 'CA-4' },
    { name: 'CA-5', value: 'CA-5' },
    { name: 'CA-6', value: 'CA-6' },
    { name: 'HI-1', value: 'HI-1' },
    { name: 'HI-2', value: 'HI-2' },
    { name: 'HI-3', value: 'HI-3' },
    { name: 'HI-4', value: 'HI-4' },
    { name: 'HI-5', value: 'HI-5' },
    { name: 'HI-6', value: 'HI-6' },
    { name: 'I', value: 'I' },
    { name: 'IA', value: 'IA' },
    { name: 'II', value: 'II' },
    { name: 'MSE1', value: 'MSE1' },
    { name: 'MSE2', value: 'MSE2' },
    { name: 'MSE3', value: 'MSE3' },
    { name: 'MSE4', value: 'MSE4' },
    { name: 'MSE5', value: 'MSE5' },
    { name: 'MSE6', value: 'MSE6' },
    { name: 'N PAC', value: 'N PAC' },
    { name: 'N10_A', value: 'N10_A' },
    { name: 'N10_B', value: 'N10_B' },
    { name: 'N10_C', value: 'N10_C' },
    { name: 'N10_D', value: 'N10_D' },
    { name: 'NOAA_A', value: 'NOAA_A' },
    { name: 'NOAA_B', value: 'NOAA_B' },
    { name: 'NOAA_C', value: 'NOAA_C' },
    { name: 'NOAA_D', value: 'NOAA_D' },
    { name: 'NV_N', value: 'NV_N' },
    { name: 'NV_S', value: 'NV_S' },
    { name: 'NV_W', value: 'NV_W' },
    { name: 'TX_A', value: 'TX_A' },
    { name: 'TX_B', value: 'TX_B' },
    { name: 'TX_C', value: 'TX_C' },
    { name: 'TX_D', value: 'TX_D' },
  ];

  @observable frequencyOptions = [
    { name: '1 Year', value: '1 Year' },
    { name: '2 Year', value: '2 Year' },
    { name: '5 Year', value: '5 Year' },
    { name: '10 Year', value: '10 Year' },
    { name: '25 Year', value: '25 Year' },
    { name: '50 Year', value: '50 Year' },
    { name: '100 Year', value: '100 Year' },
  ];
  @observable stateOptions: StateDto[] = [];
  @observable countiesOptions: Rainfall[] = [];
  @observable selectedCounty: Rainfall | null = null;
  @observable dimUnitHydrogOptions = [{ name: 'Standard - 484', id: 0 }];
  @observable reversedUnitHOptions: any = [];
  @observable rDistOptionIDs: any = [];
  @observable iDNumber: number = 0;
  @observable rainfallValid: boolean = false;
  @observable workingCounty: string = '';
  @observable displayToC: string = '';
  @observable hasGottenRainfall: boolean = false;
  @observable public exportedInputs: ILabelValuePair[] = [];
  @observable public exportedResults: ILabelValuePair[] = [];
  @observable public formLabelsForExport: { [key: string]: string } = {};
  @observable public resultLabelsForExport: { [key: string]: string } = {};
  @observable public formulaTitle: string = 'Hydrology Calculator';
  @observable public image: string = '';
  @observable public exportDisabled: boolean = false;
  @observable public saveButtonPopup: boolean = false;

  @action.bound toggleHeaderSwitch = async () => {
    this.headerSwitch = !this.headerSwitch;
    this.resultsRows = false;
    this.hydroCalculatorFormData.timeOfConcentration = '';
    this.hydroCalculatorFormData.county = '';
    this.hydroCalculatorFormData.rainfallDistributionType = '';
    //optional fields validation error removed
    this.formValidation.watershedLength = false;
    this.formValidation.formErrors.watershedLength = '';
    this.formValidation.watershedSlope = false;
    this.formValidation.formErrors.watershedSlope = '';
    this.formValidation.timeOfConcentration = false;
    this.formValidation.formErrors.timeOfConcentration = '';
    this.formValidation.county = false;
    this.formValidation.formErrors.county = '';
    this.formValidation.hasError = false;
    if (this.headerSwitch) {
      await this.getCountiesFromState(this.hydroCalculatorFormData.state);
      this.calculateTime();
    }
  };

  @action.bound async init() {
    await this.getStates();
    await this.getHydroData();
  }

  @action.bound toggleResults = () => {
    this.resultsRows = !this.resultsRows;
  };
  @action.bound reset = () => {
    MessageCenter.clearMessages();
    this.hydroCalculatorFormData.state = '';
    this.hydroCalculatorFormData.county = '';
    this.hydroCalculatorFormData.drainageArea = '';
    this.hydroCalculatorFormData.runoffCurveNumber = '';
    this.hydroCalculatorFormData.watershedLength = '';
    this.hydroCalculatorFormData.watershedSlope = '';
    this.hydroCalculatorFormData.timeOfConcentration = '';
    this.hydroCalculatorFormData.rainfallDistributionType = '';
    this.hydroCalculatorFormData.dimensionlessUnitHydrograph = '0';
    this.hydroCalculatorFormData.hydrologyCalculatorTableData = [{ freq: '', rain: '' }];
    this.resultsRows = false;
    this.resultsRowsData = [];
    this.resultsRowsDataUseDatabase = [];

    this.formValidation = {
      hasError: true,
      state: false,
      county: false,
      drainageArea: false,
      runoffCurveNumber: false,
      watershedLength: false,
      watershedSlope: false,
      timeOfConcentration: false,
      rainfallDistributionType: false,
      dimensionlessUnitHydrograph: false,
      hydrologyCalculatorTableData: [
        {
          freq: true,
          rain: true,
        },
      ],
      formErrors: {
        state: '',
        county: '',
        drainageArea: '',
        runoffCurveNumber: '',
        watershedLength: '',
        watershedSlope: '',
        timeOfConcentration: '',
        rainfallDistributionType: '',
        dimensionlessUnitHydrograph: '',
        hydrologyCalculatorTableData: [
          {
            freq: '',
            rain: '',
          },
        ],
      },
    };
  };

  @action.bound async handleStateInputChange(event) {
    this.hydroCalculatorFormData.county = '';
    this.countiesOptions = [];
    this.handleInputChange(event);
    await this.changeStateSelection();
    if (!this.hydroCalculatorFormData.state) {
      this.hydroCalculatorFormData.county = '';
    }
  }

  @action.bound handleOnKeyDown(event) {
    const invalidKeys = ['e', 'E', '+', '-'];
    if (invalidKeys.includes(event.key)) {
      event.preventDefault();
    } else if (event.code === 'Escape') {
      const focused = document.activeElement as HTMLElement;
      if (focused) focused.blur();
    }
  }

  @action.bound async getStates() {
    try {
      const response = await StateCountyController.getAllStatesUsingGET();
      this.stateOptions = response.data;
      MessageCenter.clearMessages();
    } catch (error) {
      MessageCenter.addError((error as Error).message);
    }
  }

  @action.bound async changeStateSelection() {
    this.hydroCalculatorFormData.county = '';
    const state = this.hydroCalculatorFormData.state;
    if (state === '') {
      this.hydroCalculatorFormData.county = '';
      // const countyInput: any = document.getElementById('county');
      // if (countyInput) {
      //   countyInput.value = '';
      // }
      return;
    }
    if (this.headerSwitch) {
      await this.getCountiesFromState(state);
    }
  }

  @action.bound async getCountiesFromState(state) {
    if (state) {
      try {
        const response = await HydroControllerApi.getCountyRegionRainfallDataByStateUsingGET(state)
        this.countiesOptions = response.data;

        // reset selection if not in counties
        const county = this.countiesOptions.find((d) => d.county === this.hydroCalculatorFormData.county);
        if (county === undefined) {
          this.hydroCalculatorFormData.county = '';
          const countyInput: any = document.getElementById('county');
          if (countyInput) {
            countyInput.value = '';
          }
        }
        MessageCenter.clearMessages();
      } catch (error) {
        MessageCenter.addError((error as Error).message);
      }
    }
  }

  @action.bound async getHydroData() {
    // following same steps as hydrology results
    const tempDuhOption = [{ name: 'Standard - 484', id: 0 }];
    const rainfallDistributions: any = [];
    try {
      const response = await Efh2ControllerApi.getAllEfh2RainHydgsUsingGET();
      const data = response.data as IEfh2RainHydgsByIdData[];
      // pull out ids 1-12 as options
      if (data.length) {
        for (const element of data) {
          if (element.id >= 1 && element.id <= 13) {
            const elementName = element.fileName.replace('.duh', '');
            const option = { id: element.id, name: elementName };
            tempDuhOption.push(option);
          }
          else {
            if (element.fileTypeCode === "RFTYPE") {
              const elemName: string = element.fileName.replace('.tbl', '');
              const fileName: string = elemName.split(' ')[1];
              const options: any = { id: element.id, name: fileName };
              rainfallDistributions.push(options);
            }
          }
        }
        tempDuhOption.sort((a, b) => {
          if (!a.name.split('_')[1] || !b.name.split('_')[1]) {
            return 0;
          } else {
            return parseInt(a.name.split('_')[1]) - parseInt(b.name.split('_')[1]);
          }
        });
        this.dimUnitHydrogOptions = tempDuhOption;
        this.hydroCalculatorFormData.dimensionlessUnitHydrograph = tempDuhOption[0].id.toString();
        this.reversedUnitHOptions = tempDuhOption;
        this.rDistOptionIDs = rainfallDistributions;
      }
    } catch (error) {
      console.warn('getHydroData: error =', error);
    }
  }

  get tableValidationState() {
    return this.formValidation.hydrologyCalculatorTableData.reduce((ongoing, value) => {
      ongoing.freq = ongoing.freq || value.freq;
      ongoing.rain = ongoing.rain || value.rain;
      return ongoing;
    });
  }

  get validateRequiredInputs() {
    let hasError = false;
    for (const element in this.formValidation) {
      if (element === 'hasError' || element === 'hydrologyCalculatorTableData' || element === 'formErrors') {
        continue;
      }
      else {
        hasError = hasError || this.formValidation[element];
      }
    }
    if (!this.headerSwitch) {
      hasError = hasError || this.tableValidationState.freq || this.tableValidationState.rain;
    }
    return !hasError;
  }

  @action.bound async validateReqInput() {
    // Required
    this.formValidation.hasError = false;
    await this.validateInput('state', this.hydroCalculatorFormData.state);
    await this.validateInput('county', this.hydroCalculatorFormData.county);
    await this.validateInput('drainage-area', this.hydroCalculatorFormData.drainageArea);
    await this.validateInput('runoff-curve-number', this.hydroCalculatorFormData.runoffCurveNumber);
    await this.validateInput('rainfall-distribution-type', this.hydroCalculatorFormData.rainfallDistributionType);
    await this.validateInput('dimensionless-unit-hydrograph', this.hydroCalculatorFormData.dimensionlessUnitHydrograph);
    // Optional
    if (!this.headerSwitch) {
      await this.validateInput('time-of-concentration', this.hydroCalculatorFormData.timeOfConcentration);
      await this.validateTable();
      await this.validateInput('watershed-slope', this.hydroCalculatorFormData.watershedSlope);
      await this.validateInput('watershed-length', this.hydroCalculatorFormData.watershedLength);
    } else {
      await this.validateInput('watershed-slope', this.hydroCalculatorFormData.watershedSlope);
      await this.validateInput('watershed-length', this.hydroCalculatorFormData.watershedLength);
    }
  }

  @action.bound async validateTable() {
    await this.hydroCalculatorFormData.hydrologyCalculatorTableData.forEach(
      async (inputs: IHydroCalculatorTableData, index: number) => {
        const length = this.hydroCalculatorFormData.hydrologyCalculatorTableData.length;
        if (index !== length - 1 || length === 1) {
          await this.validateInput(`table-inputs-freq-${index}`, inputs.freq, index);
          await this.validateInput(`table-inputs-rain-${index}`, inputs.rain, index);
        }
      }
    );
  }

  @action.bound async calculateResults() {
    //Hardcoded values for display
    this.isCalculating = true;
    MessageCenter.blockCalls([]);
    await this.validateReqInput();
    MessageCenter.unblockCalls();
    if (this.validateRequiredInputs) {
      try {
        const payload = this.constructPayload();
        const response = await HydroControllerApi.generateWinTR20ResultsQuickHydroToolUsingPOST(payload);
        if (response && response.data && (response.data.statusMessage?.toLowerCase() !== 'success' || (response.data as any).empty)) {
          await this.processError(response);
        }
        else {
          await this.processResponse(response);
        }
      }
      catch {
        await this.processError();
      }
    } else {
      this.resultsRows = false;
      await MessageCenter.addNotificationFromCode('Common_26');
    }
    this.isCalculating = false;
  }

  @action.bound constructPayload(): Efh2InputParamsQuickHydroToolReq {
    // base shared payload
    const fullFrequency = 'YR_1, YR_2, YR_5, YR_10, YR_25, YR_50, YR_100';
    const payload: Efh2InputParamsQuickHydroToolReq = {
      county: '',
      dimUnitHydrog: this.reversedUnitHOptions[this.hydroCalculatorFormData.dimensionlessUnitHydrograph].name,
      dimUnitHydrogID: this.reversedUnitHOptions[this.hydroCalculatorFormData.dimensionlessUnitHydrograph].id,
      drainageArea: this.hydroCalculatorFormData.drainageArea !== '' ? this.hydroCalculatorFormData.drainageArea : undefined,
      manualUserInput: (!this.headerSwitch).toString(),
      rainfallType: this.hydroCalculatorFormData.rainfallDistributionType,
      runoffCN: this.hydroCalculatorFormData.runoffCurveNumber !== '' ? this.hydroCalculatorFormData.runoffCurveNumber : undefined,
      state: this.hydroCalculatorFormData.state,
      timeC: this.hydroCalculatorFormData.timeOfConcentration !== '' ? this.hydroCalculatorFormData.timeOfConcentration : undefined,
      watershedLen: this.hydroCalculatorFormData.watershedLength !== '' ? this.hydroCalculatorFormData.watershedLength : undefined,
      watershedSlope: this.hydroCalculatorFormData.watershedSlope !== '' ? this.hydroCalculatorFormData.watershedSlope : undefined,
    }
    if (this.headerSwitch) {
      payload.stormFrequency = fullFrequency;
      payload.county = this.selectedCounty?.county;
    }
    else {
      const [freq, prec] = this.convertTableInputs();
      payload.county = this.hydroCalculatorFormData.county;
      payload.prec = prec;
      payload.stormFrequency = freq;
    }
    return payload;
  }
  @action.bound convertTableInputs(): [string, string] {
    const frList: string[] = [];
    const rainList: number[] = [];
    const freqList: string[] = [];
    const finalRow: number = this.hydroCalculatorFormData.hydrologyCalculatorTableData.length - 1;
    this.hydroCalculatorFormData.hydrologyCalculatorTableData.forEach(/*async*/(value: IHydroCalculatorTableData, index: number) => {
      if (index !== finalRow && this.hydroCalculatorFormData.hydrologyCalculatorTableData[0].freq) {
        frList.push(this.hydroCalculatorFormData.hydrologyCalculatorTableData[index].freq!);
        rainList.push(this.hydroCalculatorFormData.hydrologyCalculatorTableData[index].rain !== '' ?
          this.hydroCalculatorFormData.hydrologyCalculatorTableData[index].rain as number : 0);
      }
    });
    frList.forEach(/*async*/(value: string, index: number) => {
      if (index !== finalRow) {
        freqList.push('YR_' + parseInt((frList[index].split(' ')[0])));
      }
    });
    return [freqList.join(', '), rainList.join(', ')];
  }
  @action.bound async processResponse(response: AxiosResponse<Efh2OutputParamsQuickHydroTool>) {
    // should load full table & time, else only last columns
    const { precList, peakFlowList, runoffList, timeC } = response.data;
    if (precList && peakFlowList && runoffList && timeC) {
      if (this.headerSwitch) {
        this.resultsRowsDataUseDatabase = [
          { freq: '1 Year', rain: precList[0], peakFlowQ: peakFlowList[0], runoff: runoffList[0] },
          { freq: '2 Year', rain: precList[1], peakFlowQ: peakFlowList[1], runoff: runoffList[1] },
          { freq: '5 Year', rain: precList[2], peakFlowQ: peakFlowList[2], runoff: runoffList[2] },
          { freq: '10 Year', rain: precList[3], peakFlowQ: peakFlowList[3], runoff: runoffList[3] },
          { freq: '25 Year', rain: precList[4], peakFlowQ: peakFlowList[4], runoff: runoffList[4] },
          { freq: '50 Year', rain: precList[5], peakFlowQ: peakFlowList[5], runoff: runoffList[5] },
          { freq: '100 Year', rain: precList[6], peakFlowQ: peakFlowList[6], runoff: runoffList[6] },
        ];
        this.displayToC = timeC.toString();
        this.resultsRows = true;
      }
      else {
        const rows: number = this.hydroCalculatorFormData.hydrologyCalculatorTableData.length - 1;
        this.resultsRowsData = [];
        this.hydroCalculatorFormData.hydrologyCalculatorTableData.forEach(/*async*/(inputs: IHydroCalculatorTableData, index: number) => {
          if (index !== rows) {
            this.resultsRowsData.push({ peakFlowQ: peakFlowList[index], runoff: runoffList[index] });
          }
        });
        this.resultsRows = true;
      }
    } else {
      this.processError(response);
    }
  }
  @action.bound async processError(response?: AxiosResponse<Efh2OutputParamsQuickHydroTool>) {
    this.resultsRows = false;
    MessageCenter.unblockCalls();
    if (response && response.data && response.data.statusMessage) {
      await MessageCenter.addNotificationFromCode(response.data.statusMessage);
    }
    else {
      if (!(response && response.data) || Object.keys(response.data).length === 1) {
        this.resultsRowsData = [];
        MessageCenter.unblockCalls();
        await MessageCenter.addNotificationFromCode_object({ code: 'Common_27' });
      }
    }
  }

  @action.bound handleInputChange(event, index?) {
    const { name, value } = event.target;
    switch (name) {
      case 'state':
        this.hydroCalculatorFormData.state = value;
        if (this.headerSwitch) {
          this.hydroCalculatorFormData.county = '';
          this.hydroCalculatorFormData.rainfallDistributionType = '';
        }
        break;
      case 'county':
        this.hydroCalculatorFormData.county = value;
        if (this.headerSwitch) {
          this.selectedCounty = this.countiesOptions.find(county => county.id === parseInt(value)) || null;
          this.hydroCalculatorFormData.rainfallDistributionType = this.selectedCounty?.rainfallType!;
        }
        break;
      case 'drainage-area':
        this.hydroCalculatorFormData.drainageArea = value;
        break;
      case 'runoff-curve-number':
        this.hydroCalculatorFormData.runoffCurveNumber = value;
        break;
      case 'watershed-length':
        this.hydroCalculatorFormData.watershedLength = value;
        break;
      case 'watershed-slope':
        this.hydroCalculatorFormData.watershedSlope = value;
        break;
      case 'time-of-concentration':
        this.hydroCalculatorFormData.timeOfConcentration = value;
        this.hasGottenRainfall = false;
        break;
      case 'rainfall-distribution-type':
        this.hydroCalculatorFormData.rainfallDistributionType = value;
        break;
      case 'dimensionless-unit-hydrograph':
        this.hydroCalculatorFormData.dimensionlessUnitHydrograph = value;
        break;
      case `table-inputs-freq-${index}`:
        this.formValidation.formErrors.hydrologyCalculatorTableData[index].freq = '';
        this.hydroCalculatorFormData.hydrologyCalculatorTableData[index].freq = value;
        this.addNewRow(index, value, name);
        break;
      case `table-inputs-rain-${index}`:
        this.formValidation.formErrors.hydrologyCalculatorTableData[index].rain = '';
        this.hydroCalculatorFormData.hydrologyCalculatorTableData[index].rain = value;
        this.addNewRow(index, value, name);
        break;
    }
  }

  @action.bound addNewRow(index: number, value: string | number, name: string) {
    if (
      index === this.hydroCalculatorFormData.hydrologyCalculatorTableData.length - 1 &&
      ((name === `table-inputs-freq-${index}` && value !== '') ||
        (name === `table-inputs-rain-${index}` && (value as number) >= 0))
    ) {
      this.hydroCalculatorFormData.hydrologyCalculatorTableData.push({ ...defaultRow });
      this.formValidation.hydrologyCalculatorTableData.push({ freq: false, rain: false });
      this.formValidation.formErrors.hydrologyCalculatorTableData.push({ freq: '', rain: '' });
    }
  }

  @action.bound hydroCalcErrorClass(field) {
    let localError = '';
    if (field.includes('table-inputs-')) {
      const index = +field.split('-').pop();
      if (field.includes('-freq-')) {
        localError = this.formValidation.formErrors.hydrologyCalculatorTableData[index].freq;
        return localError?.length === 0 ? 'table-inputs' : ' usa-input--error';
      } else if (field.includes('-rain-')) {
        localError = this.formValidation.formErrors.hydrologyCalculatorTableData[index].rain;
        return localError?.length === 0 ? 'table-inputs' : ' usa-input--error';
      }
    }
    else if (field === 'timeOfConcentration') {
      if (!this.headerSwitch) {
        return this.formValidation.formErrors.timeOfConcentration.length === 0 ? '' : 'usa-input--error';
      }
      else {
        return 'no-border';
      }
    }
    else if (field === 'watershedLength' || field === 'watershedSlope') {
      localError = this.formValidation.formErrors[field];
      return localError?.length === 0 ? '' : 'usa-input--error';
    }
    else {
      localError = this.formValidation.formErrors[field];
      return localError?.length === 0 ? '' : 'usa-input--error';
    }
  }

  @action.bound async calculateTime() {
    if (!this.headerSwitch) {
      return;
    }
    const invalid = this.formValidation.watershedLength || this.formValidation.watershedSlope || this.formValidation.runoffCurveNumber;
    const empty = this.hydroCalculatorFormData.watershedLength === '' || this.hydroCalculatorFormData.watershedSlope === ''
      || this.hydroCalculatorFormData.runoffCurveNumber === '';
    if (empty || invalid) {
      return;
    }
    else {
      this.hydroCalculatorFormData.timeOfConcentration = timeOfConcLagCalc(this.hydroCalculatorFormData.watershedLength,
        this.hydroCalculatorFormData.runoffCurveNumber, this.hydroCalculatorFormData.watershedSlope);
      await this.validateTime(this.hydroCalculatorFormData.timeOfConcentration);
    }
  }

  @action.bound async validateInput(name, value, index?) {
    const roundedValue = value !== '' ? formatRound(parseFloat(value), name === 'watershed-slope' ? 4 : 2) : value;
    switch (name) {
      case 'state':
        await this.commonErrorHandling('state', value === '', 'Common_26');
        break;
      case 'county':
        if (!this.headerSwitch) { break; }
        await this.commonErrorHandling('county', value === '', 'Common_26');
        break;
      case 'drainage-area':
        await this.commonErrorHandling('drainageArea', value === '' || value === undefined || roundedValue <= 0, 'Common_10', 'Drainage Area');
        this.hydroCalculatorFormData.drainageArea = roundedValue;
        break;
      case 'runoff-curve-number':
        if (value === '' || value === undefined || (roundedValue < 30 || roundedValue > 100)) {
          this.formValidation['runoffCurveNumber'] = true;
          this.formValidation.formErrors['runoffCurveNumber'] = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Hydro_48');
        }
        else {
          this.formValidation['runoffCurveNumber'] = false;
          this.formValidation.formErrors['runoffCurveNumber'] = '';
          this.formValidation.hasError = false;
        }
        await this.calculateTime();
        this.hydroCalculatorFormData.runoffCurveNumber = roundedValue;
        break;
      case 'watershed-length':
        this.hydroCalculatorFormData.watershedLength = roundedValue;
        await this.commonErrorHandling(
          'watershedLength',
          ((this.headerSwitch && (value === '' || value === undefined)) ||
            (value !== '' && value !== undefined && (roundedValue < 200 || roundedValue > 26000))),
          'Hydro_14',
        );
        if (this.headerSwitch) {
          await this.calculateTime();
        }
        break;
      case 'watershed-slope':
        this.hydroCalculatorFormData.watershedSlope = roundedValue;
        await this.commonErrorHandling(
          'watershedSlope',
          ((this.headerSwitch && (value === '' || value === undefined)) || (value !== '' && value !== undefined && roundedValue <= 0)),
          'Common_10',
          'Watershed Slope'
        );
        if (this.headerSwitch) {
          await this.calculateTime();
        }
        break;
      case 'time-of-concentration':
        if (!this.headerSwitch) {
          await this.validateTime(value);
        }
        else {
          this.formValidation['timeOfConcentration'] = false;
          this.formValidation.formErrors['timeOfConcentration'] = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'rainfall-distribution-type':
        await this.commonErrorHandling('rainfallDistributionType', value === '', 'Common_26');
        break;
      case 'dimensionless-unit-hydrograph':
        await this.commonErrorHandling('dimensionlessUnitHydrograph', value === '', 'Common_26');
        break;
      case `table-inputs-freq-${index}`:
        await this.tableErrorHandling('freq', index, value === '' || Number.isNaN(value), 'Common_26');
        break;
      case `table-inputs-rain-${index}`:
        await this.tableErrorHandling(
          'rain',
          index,
          value === undefined || value === '' || value <= 0 || Number.isNaN(value),
          'Common_10',
          '24-hr Rain'
        );
        this.hydroCalculatorFormData.hydrologyCalculatorTableData[index].rain = roundedValue;
        break;
    }
  }
  @action.bound roundRainValue(event, index) {
    const { value } = event.target;
    let roundedValue: number | '' = '';
    if (value === '' || value === undefined) {
      roundedValue = value;
    }
    else {
      roundedValue = formatRound(parseFloat(value), 2);
    }
    this.hydroCalculatorFormData.hydrologyCalculatorTableData[index].rain = roundedValue;
  }
  @action.bound async commonErrorHandling(variable: string, predicate: boolean, errorCode: string, inputLabel?: string) {
    if (predicate) {
      this.formValidation[variable] = true;
      this.formValidation.formErrors[variable] = ' ';
      this.formValidation.hasError = true;
      await MessageCenter.addNotificationFromCode(errorCode, undefined, inputLabel);
    } else {
      this.formValidation[variable] = false;
      this.formValidation.formErrors[variable] = '';
      this.formValidation.hasError = false;
    }
  }
  @action.bound async tableErrorHandling(
    variable: string,
    index: number,
    predicate: boolean,
    errorCode: string,
    inputLabel?: string
  ) {
    if (predicate) {
      this.formValidation.hydrologyCalculatorTableData[index][variable] = true;
      this.formValidation.formErrors.hydrologyCalculatorTableData[index][variable] = ' ';
      this.formValidation.hasError = true;
      await MessageCenter.addNotificationFromCode(errorCode, undefined, inputLabel);
    } else {
      this.formValidation.hydrologyCalculatorTableData[index][variable] = false;
      this.formValidation.formErrors.hydrologyCalculatorTableData[index][variable] = '';
      this.formValidation.hasError = false;
    }
  }
  @action.bound async validateTime(value) {
    let valid = false;
    const roundedValue = formatRound(value, 2);
    if (value === '' || value === undefined || roundedValue <= 0) {
      this.formValidation.timeOfConcentration = true;
      this.formValidation.formErrors.timeOfConcentration = ' ';
      this.formValidation.hasError = true;
      await MessageCenter.addNotificationFromCode_object({ code: 'Common_10', inputName: 'Time of Concentration' });
    }
    else if (roundedValue < 0.1) {
      this.hydroCalculatorFormData.timeOfConcentration = 0.1;
      await MessageCenter.addNotificationFromCode_object({ code: 'Hydro_108' });
      valid = true;
    }
    else if (roundedValue > 10) {
      this.hydroCalculatorFormData.timeOfConcentration = 10;
      this.formValidation.timeOfConcentration = true;
      this.formValidation.formErrors.timeOfConcentration = ' ';
      this.formValidation.hasError = true;
      await MessageCenter.addNotificationFromCode_object({ code: 'Hydro_15' });
    }
    else {
      valid = true;
    }
    if (valid) {
      this.formValidation.timeOfConcentration = false;
      this.formValidation.formErrors.timeOfConcentration = '';
    }
  }

  @action.bound launchAndExport() {
    // Disable export button
    this.exportDisabled = true;
    this.exportedInputs = [];
    this.exportedResults = [];

    // Initialize form labels for export
    this.formLabelsForExport = {
      state: 'State',
      county: 'County',
      drainageArea: 'Drainage Area (sq mi)',
      runoffCurveNumber: 'Runoff Curve Number',
      watershedLength: 'Watershed Length (ft)',
      watershedSlope: 'Watershed Slope (%)',
      timeOfConcentration: 'Time of Concentration (hr)',
      rainfallDistributionType: 'Rainfall Distribution Type',
      dimensionlessUnitHydrograph: 'Dimensionless Unit Hydrograph'
    };

    // Initialize result labels for export
    this.resultLabelsForExport = {
      freq: 'Frequency',
      rain: '24-Hr Rain (in)',
      peakFlowQ: 'Peak Flow Q (cfs)',
      runoff: 'Runoff (in)',
      timeC: 'Time of Concentration (hr)'
    };

    // Export form inputs
    for (const [key, value] of Object.entries(this.hydroCalculatorFormData)) {
      if (key === 'hydrologyCalculatorTableData') {
        continue;
      }

      const label = this.formLabelsForExport[key];
      if (label && value !== '' && value !== undefined) {
        let exportValue = value.toString();

        // Convert state code to full state name for display
        if (key === 'state' && this.stateOptions.length > 0) {
          const stateOption = this.stateOptions.find(state => state.state_code === value);
          if (stateOption && stateOption.state_name) {
            exportValue = stateOption.state_name;
          }
        }

        // Convert dimensionless unit hydrograph ID to descriptive name
        if (key === 'dimensionlessUnitHydrograph' && this.reversedUnitHOptions.length > 0) {
          const duhOption = this.reversedUnitHOptions.find(option => option.id.toString() === value);
          if (duhOption && duhOption.name) {
            exportValue = duhOption.name;
          }
        }

        this.exportedInputs.push({
          label: label,
          value: exportValue
        });
      }
    }


    // Add time of concentration to inputs if using database results
    if (this.headerSwitch && this.displayToC) {
      this.exportedInputs.push({
        label: this.resultLabelsForExport.timeC,
        value: this.displayToC
      });
    }

    // Export results
    if (this.headerSwitch && this.resultsRowsDataUseDatabase) {
      // Database results - format as table data with proper structure
      const tableHeaders = 'Frequency (yr), 24-hr Rain, Peak Flow Q (cfs), Runoff (in)';

      this.exportedResults = this.resultsRowsDataUseDatabase
        .map((row) => {
          if (row.freq) {
            return {
              label: tableHeaders,
              value: {
                frequency: row.freq,
                rain: row.rain !== undefined ? row.rain.toString() : '',
                peakFlowQ: row.peakFlowQ !== undefined ? row.peakFlowQ.toString() : '',
                runoff: row.runoff !== undefined ? row.runoff.toString() : ''
              }
            };
          }
          return null;
        })
        .filter((item): item is ILabelValuePair => item !== null);

    } else if (!this.headerSwitch && this.resultsRowsData) {
      // Manual input results - format as table data with 24-hr Rain column
      const tableHeaders = 'Frequency (yr), 24-hr Rain (in), Peak Flow Q (cfs), Runoff (in)';

      this.exportedResults = this.resultsRowsData.map((row, index) => {
        const tableData = this.hydroCalculatorFormData.hydrologyCalculatorTableData;
        const freqLabel = (index < tableData.length) ? tableData[index]?.freq || `Row ${index + 1}` : `Row ${index + 1}`;
        const rainValue = (index < tableData.length) ? tableData[index]?.rain : '';

        return {
          label: tableHeaders,
          value: {
            frequency: freqLabel,
            rain: rainValue !== undefined && rainValue !== '' ? rainValue.toString() : '',
            peakFlowQ: row.peakFlowQ !== undefined ? row.peakFlowQ.toString() : '',
            runoff: row.runoff !== undefined ? row.runoff.toString() : ''
          }
        };
      });
    }
    this.printFormula();
    this.exportDisabled = false;
  }

  @action.bound printFormula() {
    this.saveButtonPopup = true;
  }
}
